// /amazon-overview/eusdr - the questions InRange needs Amazon to settle about the EUSDR, as a
// table: each with its category, the Process overview steps it bears on, the documents' own
// words behind it, and the replies either side has posted, each under its writer's name.
// docs/Y2026-Q4/design-eusdr-questions.md
import {
  createContext,
  type FormEvent,
  Fragment,
  type ReactNode,
  useContext,
  useMemo,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";

import {
  type EusdrDocument,
  type EusdrReply,
  type EusdrQuestion,
  type EusdrQuestions,
  type EusdrRuling,
  type EusdrSourceRef,
  type EusdrStatus,
  type EusdrWords,
  useEusdrQuestions,
  useRemoveEusdrReply,
  useReplyToEusdrQuestion,
} from "../api/hooks/useEusdrQuestions";
import { useReportView } from "../api/hooks/useShares";
import { SourceTextModal } from "../components/SourceTextModal";
import { OverviewHeading } from "./amazonOverview/Frame";
import { pageLabel } from "./amazonOverview/overviewPages";
import overview from "./amazonOverview/amazonOverview.module.css";
import table from "./amazonOverview/openIssues.module.css";
import {
  LETTERS,
  MARK_LABEL,
  RULINGS,
  rulingLabel,
  STATUSES,
  statusLabel,
  when,
} from "./amazonOverview/eusdrLabels";
import styles from "./amazonOverview/eusdrQuestions.module.css";
import { downloadEusdrWorkbook } from "./amazonOverview/eusdrWorkbook";

const ALL = "";
const STEP_PARAM = "step";

const STATUS_CLASS: Record<EusdrStatus, string> = {
  awaiting: styles.awaiting,
  discussion: styles.discussion,
  answered: styles.answered,
};

type SortBy = "number" | "step" | "status" | "latest";
/** The status filter: every question, those not yet answered, or those answered. */
const NOT_ANSWERED = "not_answered";
const ANSWERED = "answered";
type StatusFilter = typeof NOT_ANSWERED | typeof ANSWERED | "";

/** A conflict's two clauses side by side, the one ruled to apply highlighted. */
const ClausePair = ({ q }: { q: EusdrQuestion }) => (
  <div className={styles.pair}>
    {q.clauses.map((c, i) => {
      const applies = q.ruling === RULINGS[i];
      return (
        <div key={c.source} className={`${styles.side} ${applies ? styles.sideApplies : ""}`}>
          <div className={styles.sideHead}>
            <span className={styles.letter}>{LETTERS[i]}</span>
            {applies && <span className={styles.appliesTag}>Applies</span>}
          </div>
          <div className={styles.says}>{c.says}</div>
          <div className={styles.sideSource}>
            <SourceLine refs={c.source_refs} quotes={() => quotesIn(c.text)} />
          </div>
        </div>
      );
    })}
    {q.ruling === "neither" && (
      <div className={styles.neither}>Neither applies as written: see the replies</div>
    )}
  </div>
);

/** The response that set a conflict's standing choice: the latest with a ruling since it
 * was last reopened. */
const rulingReply = (q: EusdrQuestion): EusdrReply | undefined => {
  const lastReopen = q.replies.map((r) => r.marks).lastIndexOf("reopened");
  const since = q.replies.slice(Math.max(lastReopen, 0)).filter((r) => r.ruling);
  return since[since.length - 1];
};

/** Each clause in full, with a button to select it as the one that applies; "Post
 * response" then saves the selection with the response. The clause chosen stands green, and
 * whoever chose it can remove the choice, which takes it out of the record and reopens the
 * conflict. */
const ClauseRuling = ({
  q,
  selected,
  onSelect,
}: {
  q: EusdrQuestion;
  selected: EusdrRuling | null;
  onSelect: (ruling: EusdrRuling | null) => void;
}) => {
  const remove = useRemoveEusdrReply();
  const chosen = rulingReply(q);
  return (
    <>
      <div className={styles.pairFull}>
        {q.clauses.map((c, i) => {
          const ruling = RULINGS[i];
          const applies = q.ruling === ruling;
          const isSelected = !q.ruling && selected === ruling;
          return (
            <div
              key={c.source}
              className={[
                styles.clause,
                applies ? styles.sideApplies : "",
                isSelected ? styles.clauseSelected : "",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              <div className={styles.sideHead}>
                <span className={styles.letter}>{LETTERS[i]}</span>
                <strong>{c.says}</strong>
                {applies && <span className={styles.appliesTag}>Applies</span>}
                {applies && chosen?.mine && (
                  <button
                    type="button"
                    className={styles.removeLink}
                    disabled={remove.isLoading}
                    onClick={() => remove.mutate({ reply_id: chosen.reply_id })}
                  >
                    {remove.isLoading ? "Removing…" : "Remove"}
                  </button>
                )}
              </div>
              <div className={styles.said}>{c.text}</div>
              <div className={styles.citation}>
                <SourceLine refs={c.source_refs} quotes={() => quotesIn(c.text)} />
              </div>
              {!q.ruling && (
                <button
                  type="button"
                  className={`${styles.markButton} ${isSelected ? styles.markButtonOn : ""}`}
                  aria-pressed={isSelected}
                  onClick={() => onSelect(isSelected ? null : ruling)}
                >
                  {LETTERS[i]} applies
                </button>
              )}
            </div>
          );
        })}
      </div>
      {remove.error && (
        <span className={overview.error} role="alert">
          Not removed: {remove.error.message}
        </span>
      )}
    </>
  );
};

/** The documents InRange staff can open, and a way to open one at the quoted words. */
const Sources = createContext<{
  documents: Record<string, EusdrDocument>;
  open: (document: EusdrDocument, quotes: string[]) => void;
}>({ documents: {}, open: () => undefined });

/** Opens the documents the questions cite in the document viewer, at the quoted words. */
const SourceDocuments = ({
  documents,
  children,
}: {
  documents: Record<string, EusdrDocument>;
  children: ReactNode;
}) => {
  const [opened, setOpened] = useState<{ document: EusdrDocument; quotes: string[] } | null>(null);
  const value = useMemo(
    () => ({
      documents,
      open: (document: EusdrDocument, quotes: string[]) => setOpened({ document, quotes }),
    }),
    [documents],
  );
  return (
    <Sources.Provider value={value}>
      {children}
      {opened && (
        <SourceTextModal
          globalDataId={opened.document.global_data_id}
          hasSourceText={opened.document.has_text}
          evidence={opened.quotes}
          cited
          sourceLabel={opened.document.file_name}
          contentType={opened.document.content_type}
          onClose={() => setOpened(null)}
        />
      )}
    </Sources.Provider>
  );
};

/** The quoted passages in a line of the documents' words, to find in the document; the
 * whole line where it quotes nothing. */
const quotesIn = (text: string): string[] => {
  const quoted = [...text.matchAll(/["\u201c]([^"\u201c\u201d]{4,})["\u201d]/g)].map((m) => m[1]);
  return quoted.length ? quoted : [text];
};

/** A reference that opens its document at the quoted words, where the document is one
 * staff can open; plain text otherwise. */
const SourceLink = ({
  document,
  quotes,
  children,
}: {
  document: string | null;
  quotes: () => string[];
  children: ReactNode;
}) => {
  const { documents, open } = useContext(Sources);
  const file = document ? documents[document] : undefined;
  if (!file) return <>{children}</>;
  return (
    <button
      type="button"
      className={styles.sourceLink}
      title={`Open ${file.title} at these words`}
      onClick={() => open(file, quotes())}
    >
      {children}
    </button>
  );
};

/** A source line, each reference opening its document at the words quoted from it. */
const SourceLine = ({
  refs,
  quotes,
}: {
  refs: EusdrSourceRef[];
  quotes: (document: string) => string[];
}) => (
  <>
    {refs.map((r, i) => (
      <Fragment key={i}>
        {i > 0 && "; "}
        <SourceLink document={r.document} quotes={() => (r.document ? quotes(r.document) : [])}>
          {r.text}
        </SourceLink>
      </Fragment>
    ))}
  </>
);

/** One line of the documents' words: a cited line its citation above its words, the
 * citation opening the document there, and a lead-in line as a paragraph. */
const WordsLine = ({ w }: { w: EusdrWords }) =>
  w.citation ? (
    <div className={styles.cited}>
      <div className={styles.citation}>
        <SourceLink document={w.document} quotes={() => quotesIn(w.text)}>
          {w.citation}
        </SourceLink>
      </div>
      <div className={styles.said}>{w.text}</div>
    </div>
  ) : (
    <p className={styles.lead}>{w.text}</p>
  );

/** The documents' own words as a list. Where they come in groups (a reference and what the
 * EUSDR holds there now), each group of two or more lines sits in its own box. */
const DocumentWords = ({ words }: { words: EusdrWords[] }) => {
  const groups = words.reduce<EusdrWords[][]>((acc, w) => {
    const last = acc[acc.length - 1];
    if (last && last[0].group === w.group) last.push(w);
    else acc.push([w]);
    return acc;
  }, []);
  return (
    <div className={styles.words}>
      {groups.length === 1
        ? words.map((w, i) => <WordsLine key={i} w={w} />)
        : groups.map((g) =>
            g.length === 1 ? (
              <WordsLine key={g[0].group} w={g[0]} />
            ) : (
              <div key={g[0].group} className={styles.wordGroup}>
                {g.map((w, i) => (
                  <WordsLine key={i} w={w} />
                ))}
              </div>
            ),
          )}
    </div>
  );
};

/** How many of a question's parts are answered, as a bar, shown on the question itself. */
const PartProgress = ({
  parts,
  shown,
  onToggle,
}: {
  parts: EusdrQuestion[];
  shown: boolean;
  /** Absent where only matching parts are listed, so there is nothing to fold. */
  onToggle: (() => void) | undefined;
}) => {
  const done = parts.filter((p) => p.status === "answered").length;
  return (
    <div className={styles.progress}>
      <div className={styles.progressBar} aria-hidden>
        <span style={{ width: `${(100 * done) / Math.max(parts.length, 1)}%` }} />
      </div>
      <span>
        {done} of {parts.length} parts answered
      </span>
      {onToggle && (
        <button type="button" className={styles.partsToggle} aria-expanded={shown} onClick={onToggle}>
          {shown ? "Hide parts" : `Show ${parts.length} parts`}
        </button>
      )}
    </div>
  );
};

const count = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`;

/** The question's text: a "1." line starts a numbered part, a "- " line a bullet under
 * the lead-in, and any other line is a paragraph. */
const QuestionText = ({ text }: { text: string }) => {
  const lines = text.split("\n").filter((l) => l.trim());
  const numbered = lines.filter((l) => /^\d+\.\s/.test(l));
  const bullets = lines.filter((l) => l.startsWith("- "));
  const plain = lines.filter((l) => !numbered.includes(l) && !bullets.includes(l));
  return (
    <>
      {plain.map((l) => (
        <p key={l} className={styles.question}>
          {l}
        </p>
      ))}
      {numbered.length > 0 && (
        <ol className={styles.parts}>
          {numbered.map((l) => (
            <li key={l}>{l.replace(/^\d+\.\s*/, "")}</li>
          ))}
        </ol>
      )}
      {bullets.length > 0 && (
        <ul className={styles.parts}>
          {bullets.map((l) => (
            <li key={l}>{l.slice(2)}</li>
          ))}
        </ul>
      )}
    </>
  );
};

/** A way to take back a response one wrote, asking once before it goes. */
const RemoveReply = ({ replyId }: { replyId: string }) => {
  const remove = useRemoveEusdrReply();
  const [asking, setAsking] = useState(false);
  return (
    <div className={styles.removeRow}>
      {asking ? (
        <>
          <span>Remove this response?</span>
          <button
            type="button"
            className={styles.removeLink}
            disabled={remove.isLoading}
            onClick={() => remove.mutate({ reply_id: replyId })}
          >
            {remove.isLoading ? "Removing…" : "Remove"}
          </button>
          <button type="button" className={styles.removeLink} onClick={() => setAsking(false)}>
            Keep
          </button>
        </>
      ) : (
        <button type="button" className={styles.removeLink} onClick={() => setAsking(true)}>
          Remove
        </button>
      )}
      {remove.error && (
        <span className={overview.error} role="alert">
          Not removed: {remove.error.message}
        </span>
      )}
    </div>
  );
};

const ReplyForm = ({
  q,
  text,
  setText,
  selected,
  onPosted,
}: {
  q: EusdrQuestion;
  text: string;
  setText: (text: string) => void;
  selected: EusdrRuling | null;
  onPosted: () => void;
}) => {
  const reply = useReplyToEusdrQuestion();
  const [answered, setAnswered] = useState(false);
  const isAnswered = q.status === "answered";
  // A conflict is answered by selecting a clause on its card and posting, and reopened by
  // removing that choice, so its form has no answered or reopen tick.
  const conflict = q.clauses.length > 0;
  const choice = conflict && !q.ruling ? selected : null;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    reply.mutate(
      {
        key: q.key,
        text,
        answer: null,
        marks: choice ? "answered" : answered ? (isAnswered ? "reopened" : "answered") : null,
        ruling: choice,
      },
      {
        onSuccess: () => {
          setText("");
          setAnswered(false);
          onPosted();
        },
      },
    );
  };
  return (
    <form className={styles.form} onSubmit={submit}>
      <label className={styles.formLabel} htmlFor={`eusdr-reply-${q.key}`}>
        {q.group ? "Note on the question as a whole" : conflict ? "Response (optional)" : "Response"}
      </label>
      <textarea
        id={`eusdr-reply-${q.key}`}
        className={styles.textarea}
        value={text}
        maxLength={2000}
        rows={3}
        placeholder={
          q.group
            ? "Answer each part in its own row."
            : conflict
              ? "Select the clause that applies, add a response if you like, then post."
              : "Either side can respond. It is saved under your name."
        }
        onChange={(e) => setText(e.target.value)}
      />
      <div className={styles.formRow}>
        {!q.group && !conflict && (
          <label className={styles.check}>
            <input
              type="checkbox"
              checked={answered}
              onChange={(e) => setAnswered(e.target.checked)}
            />
            {isAnswered ? "Reopen" : "Mark as answered"}
          </label>
        )}
        <button
          type="submit"
          className={styles.post}
          disabled={(!text.trim() && !answered && !choice) || reply.isLoading}
        >
          {reply.isLoading ? "Posting…" : "Post response"}
        </button>
        {choice && (
          <span className={styles.postNote}>
            Saves {LETTERS[RULINGS.indexOf(choice)]} applies
            {text.trim() ? " with your response" : ""}
          </span>
        )}
        {reply.error && (
          <span className={overview.error} role="alert">
            Not posted: {reply.error.message}
          </span>
        )}
      </div>
    </form>
  );
};

const Detail = ({
  q,
  parent,
  colSpan,
}: {
  q: EusdrQuestion;
  parent: EusdrQuestion | undefined;
  colSpan: number;
}) => {
  const [draft, setDraft] = useState("");
  const [selected, setSelected] = useState<EusdrRuling | null>(null);
  return (
    <tr className={styles.detailRow}>
      <td colSpan={colSpan}>
        <div className={styles.detail}>
          <section className={styles.context}>
            {q.context && (
              <div className={styles.block}>
                <h3 className={styles.label}>Why we are asking</h3>
                <p className={styles.prose}>{q.context}</p>
              </div>
            )}
            {q.clauses.length > 0 ? (
              <div className={styles.block}>
                <h3 className={styles.label}>What the documents say</h3>
                <ClauseRuling q={q} selected={selected} onSelect={setSelected} />
              </div>
            ) : (
              q.verbatim && (
                <div className={styles.block}>
                  <h3 className={styles.label}>What the documents say</h3>
                  <DocumentWords words={q.words} />
                  {q.source && (
                    <p className={styles.source}>
                      Source:{" "}
                      <SourceLine
                        refs={q.source_refs}
                        quotes={(document) =>
                          q.words.filter((w) => w.document === document).flatMap((w) => quotesIn(w.text))
                        }
                      />
                    </p>
                  )}
                </div>
              )
            )}
            {parent && !q.verbatim && !q.clauses.length && (
              <p className={styles.seeParent}>
                {q.context ? "The contract wording is" : "The background and contract wording are"} on{" "}
                {parent.label}, the question this is part of.
              </p>
            )}
          </section>
          <section className={styles.thread}>
            <h3 className={styles.label}>
              {q.replies.length ? count(q.replies.length, "response", "responses") : "No responses yet"}
            </h3>
            {q.replies.map((r) => (
              <div key={r.reply_id} className={styles.reply}>
                <div className={styles.replyMeta}>
                  <strong>{r.name}</strong>
                  <span className={r.side === "amazon" ? styles.sideAmazon : styles.sideInrange}>
                    {r.side === "amazon" ? "Amazon" : "InRange"}
                  </span>
                  <span>{when(r.at)}</span>
                  {r.ruling && (
                    <span className={styles.markedAnswered}>{rulingLabel(q, r.ruling)}</span>
                  )}
                  {r.marks && !r.ruling && (
                    <span className={styles.markedAnswered}>{MARK_LABEL[r.marks]}</span>
                  )}
                </div>
                {r.text && <p className={styles.replyText}>{r.text}</p>}
                {r.mine && <RemoveReply replyId={r.reply_id} />}
              </div>
            ))}
            <ReplyForm
              q={q}
              text={draft}
              setText={setDraft}
              selected={selected}
              onPosted={() => setSelected(null)}
            />
          </section>
        </div>
      </td>
    </tr>
  );
};

/** Downloads every question, whatever the filters, as an .xlsx (eusdrWorkbook.ts). */
const DownloadButton = ({ data }: { data: EusdrQuestions }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const download = async () => {
    setBusy(true);
    setError(null);
    try {
      await downloadEusdrWorkbook(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <button
        type="button"
        className={styles.download}
        disabled={busy}
        onClick={() => void download()}
      >
        {busy ? "Preparing…" : "Download .xlsx"}
      </button>
      {error && (
        <span className={overview.error} role="alert">
          Download failed: {error}
        </span>
      )}
    </>
  );
};

const QuestionTable = ({ data }: { data: EusdrQuestions }) => {
  const [params, setParams] = useSearchParams();
  const [category, setCategory] = useState(ALL);
  const [status, setStatus] = useState<StatusFilter>(ALL);
  const [sortBy, setSortBy] = useState<SortBy>("number");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState<Set<string>>(() => new Set());
  // Questions with parts start collapsed.
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const toggleParts = (key: string) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  // The step filter lives in the URL, so a Process overview step can link to its questions.
  const step = params.get(STEP_PARAM) ?? ALL;
  const setStep = (id: string) =>
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (id) next.set(STEP_PARAM, id);
        else next.delete(STEP_PARAM);
        return next;
      },
      { replace: true },
    );

  const stepOrder = useMemo(() => new Map(data.steps.map((s, i) => [s.id, i])), [data.steps]);
  const stepTitle = (id: string) => data.steps.find((s) => s.id === id)?.title ?? id;
  const usedSteps = data.steps.filter((s) => data.questions.some((q) => q.steps.includes(s.id)));
  // A link to a step no question sits on shows every question rather than none.
  const activeStep = usedSteps.some((s) => s.id === step) ? step : ALL;
  const partsOf = (q: EusdrQuestion) => data.questions.filter((p) => p.parent === q.key);
  // A question with parts is placed by its parts too: its earliest step, its latest reply.
  const withParts = (q: EusdrQuestion) => [q, ...partsOf(q)];
  const firstStep = (q: EusdrQuestion) =>
    Math.min(...withParts(q).flatMap((x) => x.steps.map((s) => stepOrder.get(s) ?? 99)));
  const lastReply = (q: EusdrQuestion) => {
    const all = withParts(q)
      .flatMap((x) => x.replies.map((r) => ({ q: x, r })))
      .sort((a, b) => a.r.at.localeCompare(b.r.at));
    return all[all.length - 1];
  };
  const latest = (q: EusdrQuestion) => lastReply(q)?.r.at ?? "";

  const words = search.toLowerCase().split(/\s+/).filter(Boolean);
  const matchesFilters = (q: EusdrQuestion) =>
    (category === ALL || q.category === category) &&
    (activeStep === ALL || q.steps.includes(activeStep)) &&
    (status === ALL ||
      (status === ANSWERED ? q.status === "answered" : q.status !== "answered"));
  const matchesSearch = (q: EusdrQuestion) =>
    words.every((w) =>
      [
        q.topic,
        q.question,
        q.context,
        q.verbatim,
        q.source,
        q.category,
        ...q.clauses.flatMap((c) => [c.says, c.text]),
        ...q.replies.map((r) => r.text),
      ]
        .join(" ")
        .toLowerCase()
        .includes(w),
    );
  const matches = (q: EusdrQuestion) => matchesFilters(q) && matchesSearch(q);
  // A question is settled once it and every part are answered; settled questions go to the
  // bottom, whatever the sort.
  const isSettled = (q: EusdrQuestion) => withParts(q).every((x) => x.group || x.status === "answered");
  const settled = (a: EusdrQuestion, b: EusdrQuestion) => Number(isSettled(a)) - Number(isSettled(b));
  const tops = data.questions
    .filter((q) => !q.parent && (matches(q) || partsOf(q).some(matches)))
    .sort((a, b) => {
      if (settled(a, b)) return settled(a, b);
      const later = Number(a.low_priority) - Number(b.low_priority);
      if (later) return later;
      if (sortBy === "number") return a.number - b.number;
      if (sortBy === "status")
        return (
          STATUSES.findIndex((s) => s.status === a.status) -
            STATUSES.findIndex((s) => s.status === b.status) || a.number - b.number
        );
      if (sortBy === "latest") return latest(b).localeCompare(latest(a)) || a.number - b.number;
      return firstStep(a) - firstStep(b) || a.number - b.number;
    });
  // Each question, then its parts, unanswered first: all of them where it is expanded and
  // the question itself matches; the matching ones where only they brought it in.
  const shown = tops.flatMap((q) => [
    q,
    ...partsOf(q)
      .filter((p) =>
        matches(q)
          ? expanded.has(q.key) && matchesFilters(p)
          : matches(p),
      )
      .sort((a, b) => Number(a.status === "answered") - Number(b.status === "answered") || a.sub - b.sub),
  ]);
  const answerable = data.questions.filter((q) => !q.group);
  const byKey = new Map(data.questions.map((q) => [q.key, q]));
  // The last part shown under each question closes its group.
  const lastShownPart = new Set(
    shown.filter((q, i) => q.parent && shown[i + 1]?.parent !== q.parent).map((q) => q.key),
  );
  const respond = (key: string) => {
    setOpen((current) => new Set(current).add(key));
    requestAnimationFrame(() => document.getElementById(`eusdr-reply-${key}`)?.focus());
  };
  const counts = Object.fromEntries(
    STATUSES.map((s) => [s.status, answerable.filter((q) => q.status === s.status).length]),
  ) as Record<EusdrStatus, number>;
  const toggle = (key: string) =>
    setOpen((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <>
      <div className={table.toolbar}>
        <select
          id="eusdr-category"
          aria-label="Category"
          className={table.select}
          value={category}
          onChange={(e) => setCategory(e.target.value)}
        >
          <option value={ALL}>All categories</option>
          {data.categories.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <select
          id="eusdr-step"
          aria-label="Step"
          className={table.select}
          value={activeStep}
          onChange={(e) => setStep(e.target.value)}
        >
          <option value={ALL}>All steps</option>
          {usedSteps.map((s) => (
            <option key={s.id} value={s.id}>
              {s.title}
            </option>
          ))}
        </select>
        <select
          id="eusdr-status"
          aria-label="Status"
          className={table.select}
          value={status}
          onChange={(e) => setStatus(e.target.value as StatusFilter)}
        >
          <option value={ALL}>All statuses</option>
          <option value={NOT_ANSWERED}>
            Not answered ({answerable.length - counts.answered})
          </option>
          <option value={ANSWERED}>Answered ({counts.answered})</option>
        </select>
        <span className={overview.muted}>
          {count(
            data.questions.filter(
              (q) =>
                !q.group &&
                (matches(q) || (q.parent !== null && matches(byKey.get(q.parent) ?? q) && matchesFilters(q))),
            ).length,
            "question",
            "questions",
          )}
        </span>
        <div className={table.toolbarEnd}>
          <div className={table.searchBox}>
            <input
              type="search"
              aria-label="Search"
              className={`${table.select} ${table.search} ${styles.search}`}
              placeholder="Search questions"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button
                type="button"
                className={table.searchClear}
                aria-label="Clear search"
                onClick={() => setSearch("")}
              >
                ×
              </button>
            )}
          </div>
          <select
            id="eusdr-sort"
            aria-label="Sort"
            className={table.select}
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortBy)}
          >
            <option value="number">Sort by numbering</option>
            <option value="step">Sort by step</option>
            <option value="status">Sort by status</option>
            <option value="latest">Sort by latest reply</option>
          </select>
          <DownloadButton data={data} />
        </div>
      </div>
      {shown.length === 0 ? (
        <p className={overview.muted}>No question matches these filters.</p>
      ) : (
        <div className={overview.tableScroll}>
          <table className={table.table}>
            <colgroup>
              <col className={styles.colNumber} />
              <col className={styles.colCategory} />
              <col className={styles.colStep} />
              <col className={styles.colQuestion} />
              <col className={styles.colStatus} />
              <col className={styles.colReplies} />
            </colgroup>
            <thead>
              <tr>
                <th>#</th>
                <th>Category</th>
                <th>Step</th>
                <th>Question</th>
                <th>Status</th>
                <th>Responses</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((q, i) => {
                const isOpen = open.has(q.key);
                const latestReply = lastReply(q);
                const last = latestReply?.r;
                const lastOn = latestReply && latestReply.q.key !== q.key ? `${latestReply.q.label}: ` : "";
                return (
                  <Fragment key={q.key}>
                    <tr
                      className={[
                        isOpen ? styles.openRow : "",
                        q.parent ? styles.partRow : "",
                        shown[i + 1]?.parent === q.key && !isOpen ? styles.groupHead : "",
                        lastShownPart.has(q.key) ? styles.partLast : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                    >
                      <td className={q.parent ? styles.partNumber : styles.number}>
                        {q.parent && <span className={styles.branch} aria-hidden />}
                        {q.label}
                      </td>
                      <td>{!q.parent && q.category}</td>
                      <td>
                        {(!q.parent || q.steps.join() !== byKey.get(q.parent)?.steps.join()) &&
                          q.steps.map((s) => <div key={s}>{stepTitle(s)}</div>)}
                      </td>
                      <td>
                        <div className={q.parent && !q.question ? styles.partTopic : styles.topic}>
                          {q.topic}
                        </div>
                        {q.group ? (
                          <PartProgress
                            parts={partsOf(q)}
                            shown={!matches(q) || expanded.has(q.key)}
                            onToggle={matches(q) ? () => toggleParts(q.key) : undefined}
                          />
                        ) : q.clauses.length > 0 ? (
                          <>
                            <p className={styles.question}>Which applies on our buildings?</p>
                            <ClausePair q={q} />
                          </>
                        ) : (
                          <QuestionText text={q.question} />
                        )}
                        <button
                          type="button"
                          className={styles.toggle}
                          aria-expanded={isOpen}
                          onClick={() => toggle(q.key)}
                        >
                          {isOpen ? "Hide details" : "Background and contract wording"}
                        </button>
                      </td>
                      <td>
                        <span className={`${styles.status} ${STATUS_CLASS[q.status]}`}>
                          {statusLabel(q.status)}
                        </span>
                        {q.ruling && (
                          <div className={styles.answerLine}>{rulingLabel(q, q.ruling)}</div>
                        )}
                      </td>
                      <td>
                        {last ? (
                          <>
                            <div className={styles.lastBy}>
                              {last.name} · {when(last.at)}
                            </div>
                            <div className={styles.lastText}>
                              {lastOn}
                              {last.text ||
                                (last.ruling && rulingLabel(latestReply.q, last.ruling)) ||
                                (last.marks && MARK_LABEL[last.marks])}
                            </div>
                            {!q.group && !isOpen && q.replies.length > 1 && (
                              <button
                                type="button"
                                className={styles.viewMore}
                                onClick={() => toggle(q.key)}
                              >
                                View {q.replies.length - 1} more
                              </button>
                            )}
                          </>
                        ) : null}
                        {!q.group && (
                          <button
                            type="button"
                            className={styles.respond}
                            onClick={() => respond(q.key)}
                          >
                            Respond
                          </button>
                        )}
                      </td>
                    </tr>
                    {isOpen && (
                      <Detail q={q} parent={q.parent ? byKey.get(q.parent) : undefined} colSpan={6} />
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

export const AmazonEusdrQuestionsRoute = () => {
  const questions = useEusdrQuestions();
  useReportView("amazon-overview", pageLabel("eusdr"));
  return (
    <>
      <OverviewHeading title={pageLabel("eusdr")} />
      <p className={overview.sub}>
        What InRange needs Amazon to settle about the EU Amazon Solar Design, Commissioning and
        O&amp;M Requirements (Version 2025-01), Exhibit 9 (Rev 1, April 2025) and Exhibit 3.
        Open a question for its background and the documents&apos; own words. Anyone on this
        page can respond or mark a question answered, saved under their name; answered questions
        move to the bottom.
      </p>
      {questions.isLoading ? (
        <p className={overview.muted}>Loading the EUSDR questions…</p>
      ) : questions.error || !questions.data ? (
        <p className={overview.error}>Could not load the EUSDR questions. Reload to try again.</p>
      ) : (
        <SourceDocuments documents={questions.data.documents}>
          <QuestionTable data={questions.data} />
        </SourceDocuments>
      )}
    </>
  );
};
